// src/pages/Settings.jsx
import React, { useEffect, useState } from 'react';
import { FaSave, FaUpload } from 'react-icons/fa';
import { API_BASE_URL } from '../constants';
import ButtonSpinner from '../components/ButtonSpinner';

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    school_name: '',
    school_address: '',
    current_session: '',
    current_term: 'First Term', // default
    next_term_begins: '',
    school_logo: null, // for new upload
    logo_preview: '',  // current logo URL
  });

  // Fetch current school settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('authToken');

        const response = await fetch(`${API_BASE_URL}/api/web/admin/settings`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) throw new Error('Failed to load settings');

        const data = await response.json();
        if (data.success && data.settings) {
          const s = data.settings;
          setFormData({
            school_name: s.school_name || '',
            school_address: s.school_address || '',
            current_session: s.current_session || '',
            current_term: s.current_term || 'First Term',
            next_term_begins: s.next_term_begins
              ? new Date(s.next_term_begins).toISOString().split('T')[0]
              : '',
            logo_preview: s.school_logo || '',
            school_logo: null,
          });
        }
      } catch (err) {
        setError(err.message || 'Failed to load school settings');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please upload a valid image file');
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setError('Image must be less than 2MB');
        return;
      }

      setFormData((prev) => ({
        ...prev,
        school_logo: file,
        logo_preview: URL.createObjectURL(file),
      }));
      setError('');
    }
  };

  const validate = () => {
    if (!formData.school_name.trim()) return 'School name is required';
    if (!formData.school_address.trim()) return 'School address is required';
    if (!formData.current_session.trim()) return 'Current session is required';
    if (!formData.next_term_begins) return 'Next term resumption date is required';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('authToken');
      const payload = new FormData();

      payload.append('school_name', formData.school_name);
      payload.append('school_address', formData.school_address);
      payload.append('current_session', formData.current_session);
      payload.append('current_term', formData.current_term);
      payload.append('next_term_begins', formData.next_term_begins);

      if (formData.school_logo) {
        payload.append('school_logo', formData.school_logo);
      }

      const response = await fetch(`${API_BASE_URL}/api/web/admin/settings/update`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          // Do NOT set Content-Type for FormData — browser sets it with boundary
        },
        body: payload,
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to update settings');
      }

      setSuccess('School settings updated successfully!');

      // Update preview if new logo uploaded
      if (data.settings?.school_logo) {
        setFormData((prev) => ({
          ...prev,
          logo_preview: data.settings.school_logo + '?t=' + new Date().getTime(), // cache bust
        }));
      }
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 text-white">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold">School Settings</h1>
          <p className="text-blue-100">
            Configure your school information, session, term, and logo
          </p>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* School Logo */}
          <div className="flex flex-col items-center mb-6">
            <div className="relative">
              <img
                src={
                  formData.logo_preview ||
                  'https://via.placeholder.com/150?text=School+Logo'
                }
                alt="School Logo"
                className="w-32 h-32 object-contain rounded-full border-4 border-gray-200 dark:border-gray-600 shadow-lg"
              />
              <label
                htmlFor="logo-upload"
                className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700 transition"
              >
                <FaUpload />
              </label>
            </div>
            <input
              id="logo-upload"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
            <p className="mt-3 text-sm text-gray-500">
              Click to upload new logo (max 2MB)
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* School Name */}
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                School Name *
              </label>
              <input
                type="text"
                name="school_name"
                value={formData.school_name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                required
              />
            </div>

            {/* Current Session */}
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Current Session *
              </label>
              <input
                type="text"
                name="current_session"
                placeholder="e.g. 2024/2025"
                value={formData.current_session}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                required
              />
            </div>

            {/* Current Term */}
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Current Term
              </label>
              <select
                name="current_term"
                value={formData.current_term}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option>First Term</option>
                <option>Second Term</option>
                <option>Third Term</option>
              </select>
            </div>

            {/* Next Term Begins */}
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Next Term Begins *
              </label>
              <input
                type="date"
                name="next_term_begins"
                value={formData.next_term_begins}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                required
              />
            </div>

            {/* School Address */}
            <div className="form-group md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                School Address *
              </label>
              <textarea
                name="school_address"
                rows="3"
                value={formData.school_address}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                required
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4 mt-8">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <>
                  <ButtonSpinner size="small" color="white" />
                  Saving...
                </>
              ) : (
                <>
                  <FaSave />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;